import { useState } from 'react';
import { api } from '@/api/client';
import type { Vacancy } from '@/types';

interface NewVacancyModalProps {
  onClose: () => void;
  onCreated: (id: string) => void;
  /** Передана — форма работает в режиме редактирования */
  vacancy?: Vacancy | null;
}

const EXPERIENCE_OPTIONS = [
  { value: '', label: 'Не важен' },
  { value: '0-1', label: 'Без опыта / до года' },
  { value: '1-3', label: 'От 1 до 3 лет' },
  { value: '3-6', label: 'От 3 до 6 лет' },
  { value: '6+', label: 'Более 6 лет' },
];

export default function NewVacancyModal({ onClose, onCreated, vacancy }: NewVacancyModalProps) {
  const isEdit = !!vacancy;
  const [title, setTitle] = useState(vacancy?.title ?? '');
  const [companyName, setCompanyName] = useState(vacancy?.company_name ?? '');
  const [location, setLocation] = useState(vacancy?.location ?? '');
  const [salaryFrom, setSalaryFrom] = useState(vacancy?.salary_from ? String(vacancy.salary_from) : '');
  const [salaryTo, setSalaryTo] = useState(vacancy?.salary_to ? String(vacancy.salary_to) : '');
  const [experience, setExperience] = useState(vacancy?.experience_years ?? '');
  const [description, setDescription] = useState(vacancy?.description ?? '');
  const [status, setStatus] = useState<'draft' | 'active'>(
    (vacancy?.status as 'draft' | 'active') || 'draft'
  );
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!title.trim() || saving) return;
    setSaving(true);
    setError('');
    const payload = {
      title: title.trim(),
      company_name: companyName.trim() || null,
      location: location.trim() || null,
      description: description.trim() || null,
      status,
      salary_from: salaryFrom ? Number(salaryFrom) : null,
      salary_to: salaryTo ? Number(salaryTo) : null,
      salary_currency: 'RUB',
      experience_years: experience || null,
    };
    try {
      const saved = isEdit
        ? await api.put<Vacancy>(`/vacancies/${vacancy!.id}`, payload)
        : await api.post<Vacancy>('/vacancies', payload);
      onCreated(saved.id);
    } catch (err) {
      setError((err as Error).message || 'Не удалось сохранить вакансию');
      setSaving(false);
    }
  };

  return (
    <div
      className="fixed inset-0 z-50 bg-black/40 flex items-start justify-center overflow-y-auto p-4 md:p-8"
      onClick={onClose}
    >
      <div
        className="bg-white rounded-2xl w-full max-w-2xl shadow-xl"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center justify-between px-6 py-4 border-b border-background-200">
          <h3 className="text-base font-heading font-semibold text-foreground-900">{isEdit ? 'Редактирование вакансии' : 'Новая вакансия'}</h3>
          <button
            onClick={onClose}
            className="w-8 h-8 rounded-lg hover:bg-background-100 flex items-center justify-center cursor-pointer"
            aria-label="Закрыть"
          >
            <i className="ri-close-line text-foreground-500" />
          </button>
        </div>

        <form onSubmit={submit} className="px-6 py-5 space-y-4">
          <div>
            <label className="block text-xs font-medium text-foreground-600 mb-1.5">Название вакансии *</label>
            <input
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              required
              autoFocus
              placeholder="Например: Помощник по дому (с проживанием)"
              className="w-full px-4 py-2.5 rounded-xl bg-background-50 border border-background-200 text-sm focus:outline-none focus:ring-2 focus:ring-primary-400"
            />
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <label className="block text-xs font-medium text-foreground-600 mb-1.5">Компания</label>
              <input
                value={companyName}
                onChange={(e) => setCompanyName(e.target.value)}
                className="w-full px-4 py-2.5 rounded-xl bg-background-50 border border-background-200 text-sm focus:outline-none focus:ring-2 focus:ring-primary-400"
              />
            </div>
            <div>
              <label className="block text-xs font-medium text-foreground-600 mb-1.5">Локация</label>
              <input
                value={location}
                onChange={(e) => setLocation(e.target.value)}
                placeholder="Санкт-Петербург"
                className="w-full px-4 py-2.5 rounded-xl bg-background-50 border border-background-200 text-sm focus:outline-none focus:ring-2 focus:ring-primary-400"
              />
            </div>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
            <div>
              <label className="block text-xs font-medium text-foreground-600 mb-1.5">Зарплата от, ₽</label>
              <input
                type="number"
                inputMode="numeric"
                value={salaryFrom}
                onChange={(e) => setSalaryFrom(e.target.value)}
                className="w-full px-4 py-2.5 rounded-xl bg-background-50 border border-background-200 text-sm focus:outline-none focus:ring-2 focus:ring-primary-400"
              />
            </div>
            <div>
              <label className="block text-xs font-medium text-foreground-600 mb-1.5">до, ₽</label>
              <input
                type="number"
                inputMode="numeric"
                value={salaryTo}
                onChange={(e) => setSalaryTo(e.target.value)}
                className="w-full px-4 py-2.5 rounded-xl bg-background-50 border border-background-200 text-sm focus:outline-none focus:ring-2 focus:ring-primary-400"
              />
            </div>
            <div>
              <label className="block text-xs font-medium text-foreground-600 mb-1.5">Опыт</label>
              <select
                value={experience}
                onChange={(e) => setExperience(e.target.value)}
                className="w-full px-4 py-2.5 rounded-xl bg-background-50 border border-background-200 text-sm focus:outline-none focus:ring-2 focus:ring-primary-400 cursor-pointer"
              >
                {EXPERIENCE_OPTIONS.map((o) => (
                  <option key={o.value} value={o.value}>{o.label}</option>
                ))}
              </select>
            </div>
          </div>

          <div>
            <label className="block text-xs font-medium text-foreground-600 mb-1.5">Описание</label>
            <textarea
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              rows={8}
              placeholder="Обязанности, условия, требования — текст можно вставить как есть"
              className="w-full px-4 py-2.5 rounded-xl bg-background-50 border border-background-200 text-sm focus:outline-none focus:ring-2 focus:ring-primary-400 resize-y"
            />
          </div>

          <div>
            <label className="block text-xs font-medium text-foreground-600 mb-1.5">Статус</label>
            <select
              value={status}
              onChange={(e) => setStatus(e.target.value as 'draft' | 'active')}
              className="w-full sm:w-64 px-4 py-2.5 rounded-xl bg-background-50 border border-background-200 text-sm focus:outline-none focus:ring-2 focus:ring-primary-400 cursor-pointer"
            >
              <option value="draft">Черновик</option>
              <option value="active">Активна</option>
            </select>
          </div>

          <p className="text-xs text-foreground-500">
            Вопросы кандидатам и критерии оценки — в разделе «Скоринг», там же AI-генерация вопросов.
          </p>

          {error && (
            <div className="p-3 rounded-xl bg-red-50 border border-red-100 text-red-700 text-sm">{error}</div>
          )}

          <div className="flex items-center justify-end gap-3 pt-1">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2.5 rounded-xl text-sm text-foreground-600 hover:bg-background-100 transition-colors cursor-pointer"
            >
              Отмена
            </button>
            <button
              type="submit"
              disabled={saving || !title.trim()}
              className="px-5 py-2.5 rounded-xl bg-primary-500 text-white text-sm font-medium hover:bg-primary-600 transition-colors disabled:opacity-50 cursor-pointer flex items-center gap-1.5"
            >
              {saving ? <i className="ri-loader-4-line animate-spin" /> : <i className="ri-check-line" />}
              {isEdit ? 'Сохранить' : 'Создать'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
